"use client"

import { useReducer, useCallback, useEffect } from "react"
import {
  ServicePillar,
  BookingAppointment,
  LocationId,
  getLocationPricing,
  PHONE_CONSULTATION_URL,
  SERVICE_PILLARS,
} from "@/data/booking-appointments"
import { trackEvent } from "@/lib/analytics"
import { LocationStep } from "./steps/LocationStep"
import { PillarStep } from "./steps/PillarStep"
import { AppointmentTypeStep } from "./steps/AppointmentTypeStep"
import { SummaryStep } from "./steps/SummaryStep"
import { BookingProgressIndicator } from "./ui/BookingProgressIndicator"

// ============================================================
// State Management
// ============================================================

// Flow: Location -> Pillar -> Appointment Type -> Summary
type BookingState = {
  step: 1 | 2 | 3 | 4
  location: LocationId | null
  pillar: ServicePillar | null
  appointment: BookingAppointment | null
}

type BookingAction =
  | { type: "SELECT_LOCATION"; location: LocationId }
  | { type: "SELECT_PILLAR"; pillar: ServicePillar }
  | { type: "SELECT_APPOINTMENT"; appointment: BookingAppointment }
  | { type: "GO_TO_STEP"; step: 1 | 2 | 3 | 4 }
  | { type: "BACK" }
  | { type: "RESET" }

function createInitialState(initialLocation?: LocationId): BookingState {
  if (initialLocation) {
    return {
      step: 2, // Start at pillar selection
      location: initialLocation,
      pillar: null,
      appointment: null,
    }
  }
  return {
    step: 1,
    location: null,
    pillar: null,
    appointment: null,
  }
}

function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case "SELECT_LOCATION":
      return {
        ...state,
        location: action.location,
      }
    case "SELECT_PILLAR":
      return {
        ...state,
        pillar: action.pillar,
      }
    case "SELECT_APPOINTMENT":
      return {
        ...state,
        appointment: action.appointment,
      }
    case "GO_TO_STEP":
      return {
        ...state,
        step: action.step,
      }
    case "BACK":
      if (state.step === 1) return state
      if (state.step === 2) {
        // Back from pillar -> location
        return { ...state, step: 1, location: null }
      }
      if (state.step === 3) {
        // Back from appointment type -> pillar
        return { ...state, step: 2, pillar: null }
      }
      if (state.step === 4) {
        // Back from summary -> appointment type
        return { ...state, step: 3, appointment: null }
      }
      return state
    case "RESET":
      return createInitialState()
    default:
      return state
  }
}

// ============================================================
// Component Props
// ============================================================

export type BookingMode = 
  | { type: "default" }
  | { type: "location"; location: LocationId }
  | { type: "phone_consultation" }
  | { type: "pillar"; pillarId: string }
  | { type: "appointment"; pillarId: string; appointmentType: "initial" | "return" | "extended" }
  | { type: "direct_summary"; location: LocationId; pillarId: string; appointmentType: "initial" | "return" | "extended" }

// Helper to find pillar by ID
function getPillarById(pillarId: string): ServicePillar | null {
  return SERVICE_PILLARS.find(p => p.id === pillarId) || null
}

// Helper to get appointment from pillar by type
function getAppointmentFromPillar(
  pillar: ServicePillar, 
  appointmentType: "initial" | "return" | "extended"
): BookingAppointment | null {
  if (appointmentType === "extended") {
    return pillar.extended || null
  }
  return pillar[appointmentType]
}

interface BookingFlowContentProps {
  onComplete: (janeUrl: string) => void
  onClose?: () => void
  className?: string
  mode?: BookingMode
}

// ============================================================
// Main Component
// ============================================================

export function BookingFlowContent({ 
  onComplete, 
  onClose,
  className = "",
  mode = { type: "default" }
}: BookingFlowContentProps) {
  // Phone consultation mode - open Jane immediately and close modal
  useEffect(() => {
    if (mode.type === "phone_consultation") {
      trackEvent("booking_phone_consultation_click")
      window.open(PHONE_CONSULTATION_URL, "_blank")
      onClose?.()
    }
  }, [mode.type, onClose])

  // Pre-selected pillar (when coming from a pillar-specific page or appointment mode)
  const preSelectedPillar = mode.type === "pillar" 
    ? getPillarById(mode.pillarId) 
    : mode.type === "appointment" 
      ? getPillarById(mode.pillarId) 
      : mode.type === "direct_summary"
        ? getPillarById(mode.pillarId)
        : null
  
  // Pre-selected appointment type (for appointment mode - e.g., Extended Appointment)
  const preSelectedAppointmentType = mode.type === "appointment" 
    ? mode.appointmentType 
    : mode.type === "direct_summary"
      ? mode.appointmentType
      : null
  
  // For direct_summary mode, we need to compute the initial state with everything pre-filled
  const getDirectSummaryInitialState = (): BookingState | null => {
    if (mode.type !== "direct_summary") return null
    const pillar = getPillarById(mode.pillarId)
    if (!pillar) return null
    const appointment = getAppointmentFromPillar(pillar, mode.appointmentType)
    if (!appointment) return null
    return {
      step: 4,
      location: mode.location,
      pillar,
      appointment,
    }
  }
  
  // Initialize state based on mode
  const initialLocation = mode.type === "location" ? mode.location : undefined
  const directSummaryState = getDirectSummaryInitialState()
  const [state, dispatch] = useReducer(
    bookingReducer, 
    directSummaryState || createInitialState(initialLocation)
  )

  // If phone consultation mode, don't render anything (redirect already triggered)
  if (mode.type === "phone_consultation") {
    return null
  }

  // Step 1: Location selection (first user choice)
  const handleSelectLocation = useCallback((location: LocationId) => {
    dispatch({ type: "SELECT_LOCATION", location })
    trackEvent("booking_flow_start", { location })
    trackEvent("booking_location_selected", { location })
    
    // If both pillar AND appointment type are pre-selected, skip directly to step 4 (summary)
    if (preSelectedPillar && preSelectedAppointmentType) {
      const appointment = getAppointmentFromPillar(preSelectedPillar, preSelectedAppointmentType)
      if (appointment) {
        dispatch({ type: "SELECT_PILLAR", pillar: preSelectedPillar })
        dispatch({ type: "SELECT_APPOINTMENT", appointment })
        dispatch({ type: "GO_TO_STEP", step: 4 })
        trackEvent("booking_pillar_selected", { pillar: preSelectedPillar.id, preSelected: true })
        trackEvent("booking_appointment_selected", { appointment: appointment.id, preSelected: true })
        return
      }
    }
    
    // If only pillar is pre-selected, skip to step 3 (appointment selection)
    if (preSelectedPillar) {
      dispatch({ type: "SELECT_PILLAR", pillar: preSelectedPillar })
      dispatch({ type: "GO_TO_STEP", step: 3 })
      trackEvent("booking_pillar_selected", { pillar: preSelectedPillar.id, preSelected: true })
    } else {
      dispatch({ type: "GO_TO_STEP", step: 2 })
    }
  }, [preSelectedPillar, preSelectedAppointmentType])

  // Step 2: Pillar selection
  const handleSelectPillar = useCallback((pillar: ServicePillar) => {
    dispatch({ type: "SELECT_PILLAR", pillar })
    dispatch({ type: "GO_TO_STEP", step: 3 })
    trackEvent("booking_pillar_selected", { pillar: pillar.id })
  }, [])

  // Step 3: Appointment type selection (with location-specific pricing)
  const handleSelectAppointment = useCallback((appointment: BookingAppointment) => {
    dispatch({ type: "SELECT_APPOINTMENT", appointment })
    dispatch({ type: "GO_TO_STEP", step: 4 })
    trackEvent("booking_appointment_selected", { appointment: appointment.id })
  }, [])

  const handleBack = useCallback(() => {
    // Special handling for direct_summary mode: from step 4, close the modal
    if (mode.type === "direct_summary" && state.step === 4) {
      onClose?.()
      return
    }
    // Special handling for appointment pre-selection: from step 4, go back to step 1
    if (preSelectedPillar && preSelectedAppointmentType && state.step === 4) {
      dispatch({ type: "RESET" })
      return
    }
    // Special handling for pillar pre-selection: from step 3, go back to step 1
    if (preSelectedPillar && state.step === 3) {
      // Reset to step 1, clearing location but keeping pillar pre-selected
      dispatch({ type: "RESET" })
      return
    }
    dispatch({ type: "BACK" })
  }, [mode.type, preSelectedPillar, preSelectedAppointmentType, state.step, onClose])

  const handleBookNow = useCallback(() => {
    if (!state.appointment || !state.location) return

    const pricing = getLocationPricing(state.appointment, state.location)
    if (!pricing) return

    trackEvent("booking_completed", {
      appointment: state.appointment.id,
      location: state.location,
      janeUrl: pricing.janeUrl,
    })

    onComplete(pricing.janeUrl)
  }, [state.appointment, state.location, onComplete])

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Progress Indicator - show all 4 steps */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex justify-center">
          <div className="w-full max-w-lg">
            <BookingProgressIndicator
              currentStep={state.step}
              totalSteps={4}
              labels={["Location", "Service", "Appointment", "Date/Time"]}
            />
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {state.step === 1 && (
          <LocationStep
            onSelect={handleSelectLocation}
          />
        )}

        {state.step === 2 && state.location && (
          <PillarStep
            location={state.location}
            onSelect={handleSelectPillar}
            onBack={handleBack}
          />
        )}

        {state.step === 3 && state.pillar && state.location && (
          <AppointmentTypeStep
            pillar={state.pillar}
            location={state.location}
            onSelect={handleSelectAppointment}
            onBack={handleBack}
          />
        )}

        {state.step === 4 && state.appointment && state.location && (
          <SummaryStep
            pillar={state.pillar}
            appointment={state.appointment}
            location={state.location}
            onBookNow={handleBookNow}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  )
}
